import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import React from 'react';
import { Box, Typography, CircularProgress, Collapse, TextField } from '@mui/material';
import { Palette } from 'Palette';
import { Permissions } from '../../../models/admin/Permission';
import { FundInvestorStatus } from './constants';
import StyledSection from '../../styles/StyledSection';
import { SECONDARY_BUTTON_HOVER_SX, StyledButton } from '../../styles/StyledButtons';
import { StyledHeading } from '../../styles/styles';
import InvestorListRow, { InvestorListRowContact } from './InvestorListRow';
import type { BulkPromoteStage } from './investorManagementTypes';
import { pipelineStageActionLabels } from './pipelineStageActionLabels';
import FundInvestmentInvestor from '../../../models/admin/FundInvestmentData';

interface KanbanBoardProps {
  // Data props
  loaded: boolean;
  investors: FundInvestmentInvestor[];
  highlightedInvestorId?: string;
  investorFilter: string;
  onInvestorFilterChange: (filter: string) => void;

  // Permission props
  hasPermission: (permissionKey: Permissions) => boolean;

  // Action props
  onAddInvestor: () => void;
  onAddAllCandidates: () => void;
  onBulkPromote: (stage: BulkPromoteStage, investorIds?: string[]) => void;

  // Loading state props
  addAllInProgress: boolean;
  bulkPromoteInProgress: boolean;
  bulkPromoteStage?: FundInvestorStatus;
  investorActionInProgress?: string;
  actionTypeInProgress?: 'promote' | 'demote' | 'delete';

  // Data transformation props
  getContactsForStage: (
    stage: FundInvestorStatus,
    options?: { withoutInvestment?: boolean; withInvestment?: boolean },
  ) => InvestorListRowContact[];
}

interface KanbanBoardState {
  selectedIds: Set<string>;
  // The exact bulk-promote identifier (including 'LEAD_WITH_EOI', which the
  // parent normalizes down to 'LEAD' before storing/passing back as
  // bulkPromoteStage) of whichever column's button was clicked — tracked
  // locally so the loading spinner shows on the column that was actually
  // clicked, not on every column that happens to share the same stage.
  lastClickedBulkPromoteStage?: BulkPromoteStage;
  expanded: boolean;
}

class KanbanBoard extends React.Component<KanbanBoardProps, KanbanBoardState> {
  state: KanbanBoardState = {
    selectedIds: new Set(),
    expanded: true,
  };

  toggleExpanded = () => {
    this.setState((prev) => ({ expanded: !prev.expanded }));
  };

  toggleSelection = (contactId: string) => {
    this.setState((prev) => {
      const next = new Set(prev.selectedIds);
      if (next.has(contactId)) {
        next.delete(contactId);
      } else {
        next.add(contactId);
      }
      return { selectedIds: next };
    });
  };

  handleBulkRemove = (contacts: InvestorListRowContact[]) => {
    const { selectedIds } = this.state;
    contacts
      .filter((contact) => selectedIds.has(contact.contactId))
      .forEach((contact) => contact.onDelete?.());
    this.setState((prev) => {
      const next = new Set(prev.selectedIds);
      contacts.forEach((contact) => next.delete(contact.contactId));
      return { selectedIds: next };
    });
  };

  handleBulkPromote = (
    bulkPromoteStage: BulkPromoteStage,
    selectedContacts: InvestorListRowContact[],
  ) => {
    const investorIds =
      selectedContacts.length > 0
        ? selectedContacts.map((contact) => contact.entity.id)
        : undefined;
    this.setState((prev) => {
      const next = new Set(prev.selectedIds);
      selectedContacts.forEach((contact) => next.delete(contact.contactId));
      return { selectedIds: next, lastClickedBulkPromoteStage: bulkPromoteStage };
    });
    this.props.onBulkPromote(bulkPromoteStage, investorIds);
  };

  renderStageColumn(
    stage: FundInvestorStatus,
    title: string,
    options?: { withoutInvestment?: boolean; withInvestment?: boolean },
    // The identifier to report through onBulkPromote (and to pick the row
    // action labels by) — only differs from `stage` for the "Lead with EOI"
    // column (see render() below), which needs to promote the EOI subset of
    // leads rather than every lead.
    bulkPromoteStage: BulkPromoteStage = stage,
  ) {
    const contacts = this.props.getContactsForStage(stage, options);
    const hasMultipleContacts = contacts.length > 1;
    const isCandidateStage = stage === 'CANDIDATE';
    const selectedContacts = contacts.filter((c) => this.state.selectedIds.has(c.contactId));
    const selectedCount = selectedContacts.length;
    const actionsDisabled =
      this.props.addAllInProgress ||
      this.props.bulkPromoteInProgress ||
      !!this.props.investorActionInProgress;
    const isPromotingThisColumn =
      this.props.bulkPromoteInProgress &&
      this.state.lastClickedBulkPromoteStage === bulkPromoteStage;

    const { promotionLabel: promotionStageLabel = '', demotionLabel: demotionStageLabel = '' } =
      pipelineStageActionLabels(bulkPromoteStage);

    return (
      <Box className="pipeline-column" key={title}>
        <Box className="stage-header">
          <Typography className="stage-title">{title}</Typography>

          <Box className="button-group">
            {isCandidateStage &&
              this.props.hasPermission(Permissions.InvestorCandidateCreation) && (
                <>
                  <StyledButton
                    compact
                    noTopSpacing
                    onClick={this.props.onAddInvestor}
                    disabled={actionsDisabled}
                  >
                    Add
                  </StyledButton>
                  <StyledButton
                    compact
                    noTopSpacing
                    buttonVariant="inactiveOrCancel"
                    sx={SECONDARY_BUTTON_HOVER_SX}
                    onClick={this.props.onAddAllCandidates}
                    disabled={actionsDisabled}
                  >
                    {this.props.addAllInProgress ? <CircularProgress size={10} /> : 'Add All'}
                  </StyledButton>
                  {selectedCount > 0 && (
                    <StyledButton
                      compact
                      noTopSpacing
                      buttonVariant="warning"
                      onClick={() => this.handleBulkRemove(contacts)}
                    >
                      Remove ({selectedCount})
                    </StyledButton>
                  )}
                </>
              )}
            {hasMultipleContacts && (
              <StyledButton
                compact
                noTopSpacing
                buttonVariant="inactiveOrCancel"
                sx={SECONDARY_BUTTON_HOVER_SX}
                onClick={() => this.handleBulkPromote(bulkPromoteStage, selectedContacts)}
                disabled={actionsDisabled}
              >
                {isPromotingThisColumn ? (
                  <CircularProgress size={10} />
                ) : selectedCount > 0 ? (
                  `Promote Selected (${selectedCount})`
                ) : isCandidateStage ? (
                  'Bulk Promote'
                ) : (
                  'Promote All'
                )}
              </StyledButton>
            )}
          </Box>
        </Box>

        <Box className="stage-content">
          {!this.props.loaded ? (
            <Box
              sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 100 }}
            >
              <CircularProgress size={24} />
            </Box>
          ) : (
            contacts.map((contact) => {
              const isInvestorInAction = this.props.investorActionInProgress === contact.entity.id;

              return (
                <InvestorListRow
                  key={contact.contactId}
                  contact={contact}
                  promotionStageLabel={promotionStageLabel}
                  demotionStageLabel={demotionStageLabel}
                  isHighlighted={this.props.highlightedInvestorId === contact.contactId}
                  isInAction={isInvestorInAction}
                  actionType={isInvestorInAction ? this.props.actionTypeInProgress : undefined}
                  selectable={hasMultipleContacts}
                  selected={this.state.selectedIds.has(contact.contactId)}
                  onToggleSelect={() => this.toggleSelection(contact.contactId)}
                />
              );
            })
          )}
        </Box>
      </Box>
    );
  }

  render() {
    const { expanded } = this.state;

    return (
      <StyledSection tone="tone1">
        <Box
          onClick={this.toggleExpanded}
          sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', width: 'fit-content' }}
        >
          <StyledHeading sx={{ mb: 0 }}>Investment Pipeline</StyledHeading>
          {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </Box>

        <Collapse in={expanded}>
          {/* Filter Input */}
          <Box sx={{ mt: 1, mb: 2 }}>
            <TextField
              placeholder="Filter by name or email..."
              value={this.props.investorFilter}
              onChange={(e) => this.props.onInvestorFilterChange(e.target.value)}
              size="small"
              fullWidth
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'white',
                },
              }}
            />
          </Box>

          <Box
            display="flex"
            gap={2}
            sx={{
              overflow: 'auto',
              '& .highlighted-card-flash': {
                animation: 'highlightFlash 2s ease-in-out',
              },
              '@keyframes highlightFlash': {
                '0%': { boxShadow: `0 0 0 0 ${Palette.default.activeBg}` },
                '50%': { boxShadow: `0 0 0 6px ${Palette.default.activeBg}` },
                '100%': { boxShadow: `0 0 0 0 ${Palette.default.activeBg}` },
              },
              '& .pipeline-column': {
                minWidth: '13.75rem',
                maxWidth: '13.75rem',
                flex: '0 0 13.75rem',
                '& .stage-header': {
                  mb: 1,
                  '& .stage-title': {
                    fontSize: '0.8125rem',
                    fontWeight: 700,
                    color: Palette.tone1.text,
                    mb: 0.5,
                  },
                  '& .button-group': {
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 0.5,
                  },
                },
                '& .stage-content': {
                  minHeight: '120px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.75,
                },
              },
            }}
          >
            {/* Candidate Stage */}
            {this.renderStageColumn(FundInvestorStatus.CANDIDATE, 'Candidate')}

            {/* Lead Stage */}
            {this.renderStageColumn(FundInvestorStatus.LEAD, 'Lead', {
              withoutInvestment: true,
            })}

            {/* Lead with EOI Stage — bulk-promotes the EOI subset specifically,
                not every Lead, hence the explicit bulkPromoteStage override. */}
            {this.renderStageColumn(
              FundInvestorStatus.LEAD,
              'Lead with EOI',
              { withInvestment: true },
              'LEAD_WITH_EOI',
            )}

            {/* Opportunity Stage */}
            {this.renderStageColumn(FundInvestorStatus.OPPORTUNITY, 'Opportunity')}

            {/* Committed Investor Stage */}
            {this.renderStageColumn(FundInvestorStatus.COMMITTED_INVESTOR, 'Committed Investor')}
          </Box>
        </Collapse>
      </StyledSection>
    );
  }
}

export default KanbanBoard;
